// src/blogPosts.js — FX Ping blog content

const allPosts = [
  {
    slug: "what-does-strong-weak-currency-mean",
    title: "What does it mean when a currency is strong or weak?",
    subtitle: "Exchange rates in plain English — no economics degree required",
    date: "2026-03-13",
    readTime: "5 min read",
    category: "FX Explained",
    number: 1,
    illustration: "strength",
    excerpt: "When you see a headline like 'the pound is at its strongest in two years', what does strong actually mean? And why should you care?",
    content: `
When you see a headline like "the pound is at its strongest in two years" or "the dollar weakened overnight", it can sound abstract. What does strong actually mean? And why should you care?

## The rate is just a price

An exchange rate is nothing more than the price of one currency in terms of another. If 1 British pound buys 1.27 US dollars, that's the price of a pound. Simple as that.

When people say a currency is **strong**, they mean it buys more of another currency than it used to. When they say it's **weak**, it buys less.

## A concrete example

Say you're British and you want to buy something priced in US dollars.

- **Last month:** £1 = $1.20 — you pay £833 for a $1,000 item
- **Today:** £1 = $1.30 — you pay £769 for the same $1,000 item

The pound got stronger. It now buys more dollars. You save £64 on the exact same purchase. That's the real-world effect of currency strength.

Flip it around: if you're American buying something priced in pounds, a stronger pound is bad news for you — you need more dollars to buy the same amount of sterling.

## Strong for whom?

This is where it gets interesting. A strong currency isn't universally good or bad — it depends on who you are.

**Strong currency is good if you're:**
- Travelling abroad (your money goes further)
- Importing goods from other countries
- Paying off debts denominated in foreign currencies

**Strong currency is bad if you're:**
- An exporter (your goods become more expensive for foreign buyers)
- A tourist destination (fewer visitors, as it costs more to come)
- A country that relies on exports for growth

This is why central banks don't simply chase the strongest possible currency. A balance matters.

## What about the exchange rate number itself?

One thing that confuses people: a higher number doesn't always mean a stronger currency. It depends on how the rate is quoted.

USD/JPY at 150 means 1 dollar buys 150 yen. If it moves to 155, the dollar got stronger (buys more yen). But EUR/USD at 1.10 means 1 euro buys 1.10 dollars — if it moves to 1.05, the euro got weaker (buys fewer dollars).

The direction of change matters more than the number itself. On FX Ping, the base currency is always on the left — so a rising number means that base currency is getting stronger.

## The takeaway

Think of a currency's strength like purchasing power in a shop. A strong currency is a well-stocked wallet — it buys more. A weak currency is a thinner wallet — it buys less. The exchange rate is simply the price tag that tells you how much your wallet is worth in someone else's shop.
`
  },
  {
    slug: "why-do-exchange-rates-change",
    title: "Why do exchange rates change?",
    subtitle: "The simple supply and demand story behind daily rate movements",
    date: "2026-03-16",
    readTime: "5 min read",
    category: "FX Explained",
    number: 2,
    illustration: "waves",
    excerpt: "Exchange rates move constantly — sometimes by fractions of a percent, sometimes dramatically overnight. The honest answer is supply and demand, just like any other market.",
    content: `
Exchange rates move constantly — sometimes by fractions of a percent, sometimes dramatically overnight. But why? The honest answer is: supply and demand, just like any other market.

## Currencies are traded like any commodity

Every day, trillions of dollars worth of currency changes hands on the foreign exchange market — the largest financial market in the world. Banks, businesses, governments, investors, and individuals all buy and sell currencies for different reasons.

When more people want to buy a currency than sell it, its price goes up. When more people want to sell it than buy it, the price falls. That's it at the core.

## What drives that supply and demand?

Several forces push and pull at currency demand every day:

**Trade flows**
When a British company sells goods to America, the American buyer needs to convert dollars into pounds to pay for them. That buying pressure pushes the pound up slightly. Multiply this across thousands of companies and millions of transactions, and trade flows become a significant force.

**Investment flows**
If investors want to buy US stocks or property, they need dollars first. Large capital movements — a sovereign wealth fund shifting billions into a new market, for example — can move rates noticeably.

**Interest rates**
Higher interest rates make a currency more attractive to hold because you earn more on deposits. If the Bank of England raises rates while the Federal Reserve holds steady, international investors might move money into pounds to earn higher returns, pushing the pound up.

**Economic data**
Jobs numbers, inflation figures, GDP growth — all of these signal the health of an economy and affect how attractive its currency is to hold. A surprisingly strong jobs report in the US can strengthen the dollar within minutes of publication.

**Market sentiment**
Sometimes currencies move simply because traders expect them to. Expectations about the future can be as powerful as current fundamentals.

## Why does it move even when nothing seems to happen?

Because something is always happening somewhere. Currency markets run 24 hours a day, five days a week, across time zones from Tokyo to London to New York. A factory output figure released in Germany at 8am affects EUR/USD before most of America is awake.

## The key insight

Exchange rates aren't set by anyone in particular — they emerge from the collective behaviour of millions of buyers and sellers reacting to information in real time. That's why they're always moving, and why predicting them with confidence is genuinely hard, even for professionals.

You can track how rates have moved historically on FX Ping's chart pages — useful for understanding the range a currency tends to trade in, even if the future is never guaranteed.
`
  },
  {
    slug: "what-causes-big-moves-in-exchange-rates",
    title: "What causes big moves in exchange rates?",
    subtitle: "Interest rates, inflation, elections — the events that shake currency markets",
    date: "2026-03-17",
    readTime: "6 min read",
    category: "FX Explained",
    number: 3,
    illustration: "storm",
    excerpt: "Most days, exchange rates drift gradually. But occasionally rates lurch. Sterling fell 15% in a single day during Brexit. The Swiss franc jumped 20% in minutes in 2015. What causes these moments?",
    content: `
Most days, exchange rates drift gradually — small moves that barely register. But occasionally rates lurch. Sterling fell 15% against the dollar in a single day during the 2016 Brexit vote. The Swiss franc jumped 20% in minutes in January 2015. What causes these moments?

## Interest rate decisions

Nothing moves currency markets more reliably than central bank interest rate decisions — or surprises around them.

When a central bank raises interest rates, holding that currency becomes more rewarding. Global investors chase yield, moving money into that currency to earn higher returns on deposits and bonds. Demand for the currency rises, and so does its price.

The key word is *surprise*. If markets already expect a rate rise, it's often "priced in" — the currency has already moved in anticipation. The big moves happen when the decision deviates from expectations: a larger rise than forecast, or a cut nobody saw coming.

## Inflation data

Inflation and interest rates are closely linked. High inflation typically leads to higher interest rates (central banks raise rates to cool inflation), so inflation data moves currency markets because it signals what central banks might do next.

A higher-than-expected inflation print can strengthen a currency in the short term — traders anticipate rate rises ahead. Persistently high inflation that seems out of control can do the opposite, eroding confidence in the currency.

## Political events and elections

Political uncertainty is a currency's enemy. Markets dislike unpredictability — they find it hard to price assets when the policy environment is unclear.

Elections, referendums, and political crises can cause sharp moves. The Brexit vote is the clearest recent example: sterling had one of its largest single-day falls ever. Not because Brexit was necessarily bad for the UK long-term, but because it introduced enormous uncertainty that nobody could reliably price.

A new government with radical economic policies — or an unexpected political scandal — can have similar effects.

## Economic crisis and panic

In moments of global panic — a financial crisis, a pandemic, a war — investors tend to flee to what they consider "safe haven" currencies. The US dollar, Swiss franc, and Japanese yen are the traditional safe havens.

This is why the dollar often strengthens during global crises, even when the crisis starts in America. It's counterintuitive, but the dollar is seen as the world's most reliable store of value in a storm.

## The Swiss franc moment

The 2015 Swiss franc example is worth understanding. Switzerland had pegged the franc to the euro to prevent it from getting too strong. On 15 January 2015, the Swiss National Bank suddenly abandoned the peg. The franc immediately jumped 20% — one of the largest single-day moves ever seen in a major currency.

The lesson: when artificial constraints break, markets move violently to find the "real" price.

## What this means for you

If you're converting a large amount of money — for a property purchase abroad, a business payment, or a major transfer — keeping an eye on upcoming economic events matters. Rate announcements, election results, and data releases are all published on economic calendars in advance.

You can't predict the outcome, but you can at least know when the risk of a big move is elevated.
`
  },
  {
    slug: "what-is-the-mid-market-rate",
    title: "What is the mid-market rate — and why don't you get it?",
    subtitle: "The rate you see on Google isn't the rate you get. Here's why.",
    date: "2026-03-18",
    readTime: "5 min read",
    category: "FX Explained",
    number: 4,
    illustration: "midpoint",
    excerpt: "You look up EUR/USD on Google: 1.0850. You go to your bank. You get 1.0620. What happened to the other 0.0230? The answer is the mid-market rate.",
    content: `
You look up EUR/USD on Google: 1.0850. You go to your bank to convert euros to dollars. You get 1.0620. What happened to the other 0.0230?

The answer is the mid-market rate — and understanding it is the single most useful thing you can learn about foreign exchange.

## What the mid-market rate actually is

Every currency pair has two prices at any given moment: a **buy price** and a **sell price**. Banks and currency traders buy at a lower price and sell at a higher price — the difference between them is the spread, and it's how they make money.

The mid-market rate (sometimes called the interbank rate or spot rate) sits exactly halfway between the buy and sell price. It's the fairest possible representation of a currency's value at a given moment — but it's a theoretical rate that exists between large banks trading with each other in enormous volumes.

For practical purposes: it's the rate you see on Google, Reuters, Bloomberg, and FX Ping. It's the reference point. It's not the rate you'll get when you actually exchange money.

## Why you don't get it

When you exchange money — at a bank, a bureau de change, or even through a service like Wise — the provider adds their margin. They move the rate away from the mid-market rate by a percentage, and that difference is their revenue.

Your bank might show you a rate of 1.0620 when the mid-market rate is 1.0850. The 0.0230 gap — about 2.1% — is their cut. On a €10,000 transfer, that's €210 disappearing silently.

Some providers are transparent about this. Many aren't.

## Why the mid-market rate matters

It's the benchmark. Knowing the mid-market rate tells you what you *should* get in a perfect world, so you can calculate exactly how much margin you're being charged.

The formula is simple:

**Margin % = (mid-market rate − your rate) ÷ mid-market rate × 100**

A 0.5% margin is good. 1–2% is average. Anything over 3% is expensive — though high-street banks routinely charge 3–5% and sometimes more.

## How FX Ping uses it

The rates on FX Ping are mid-market rates — the real, unbiased benchmark. The margin calculator lets you enter the spread your bank or provider charges, so you can see the actual converted amount you'd receive after their cut.

It's not that the rate shown is "wrong" — it's the correct reference rate. The gap between that and what you get in practice is the cost of the transaction. Knowing that gap is knowing what you're actually paying.

## The practical takeaway

Next time you need to convert money, look up the mid-market rate first on FX Ping or Google. Then compare it to the rate you're being offered. The percentage difference is your true cost — and it's often much larger than the "zero commission" fee your bank advertises.
`
  },
  {
    slug: "what-is-fx-margin",
    title: "What is FX margin and how does it cost you money?",
    subtitle: "The hidden charge buried inside every exchange rate",
    date: "2026-03-19",
    readTime: "5 min read",
    category: "FX Explained",
    number: 5,
    illustration: "margin",
    excerpt: "Most people focus on fees when sending money abroad. 'Zero commission!' sounds great. But the margin buried inside the exchange rate is usually far bigger — and far less visible.",
    content: `
Most people focus on fees when sending money abroad. "Zero commission!" sounds great. But the fee is rarely the real cost. The margin buried inside the exchange rate is usually far bigger — and far less visible.

## What FX margin is

FX margin is the percentage difference between the mid-market rate (the real, interbank rate) and the rate a provider offers you.

If the mid-market rate for GBP/USD is 1.2700 and your bank offers you 1.2446, the margin is:

**(1.2700 − 1.2446) ÷ 1.2700 × 100 = 2%**

On £5,000, a 2% margin costs you £100. On £50,000 — say, a property deposit abroad — it's £1,000. Gone, silently, inside the exchange rate.

## Why it's invisible

Banks and currency providers discovered long ago that people are far more sensitive to visible fees than to rate differences. "No fees" reads as "free." But the margin in the rate is the fee — it's just been moved somewhere less noticeable.

Regulations in many countries now require providers to disclose the exchange rate they're using, but there's no requirement to prominently display the margin percentage. So most people never calculate it.

## How different providers compare

As a rough guide:
- **High-street banks:** 2.5% to 5% margin, sometimes more
- **Airport bureaux de change:** 5% to 10% (sometimes worse)
- **Online transfer services (Wise, OFX):** 0.3% to 1%
- **Specialist FX brokers:** 0.1% to 0.5% on large amounts

The differences are stark. On a £20,000 transfer, a 4% bank margin versus a 0.5% specialist margin is a £700 difference.

## The "zero fee" trap

A provider advertising "zero commission" or "no fees" is not necessarily offering a good deal — they may simply be making their money entirely through the margin instead of a separate fee. Always compare the actual exchange rate to the mid-market rate, not just the fee line.

## How to calculate it yourself

1. Look up the mid-market rate on FX Ping or Google
2. Note the rate your provider is offering
3. Apply the formula: **(mid-market − offered) ÷ mid-market × 100**

That percentage is your true cost.

FX Ping's margin calculator does this in reverse — enter your margin percentage, and the table shows you what each converted amount looks like after the markup is applied. Useful for comparing what different providers would actually deliver into your account.

## The takeaway

FX margin is the main way currency providers make money on retail transactions. It's not inherently bad — businesses need to profit — but being aware of it means you can compare providers honestly and avoid paying more than you need to.
`
  },
  {
    slug: "why-are-weekend-exchange-rates-different",
    title: "Why are weekend exchange rates different?",
    subtitle: "Markets close. Rates freeze. Banks don't always tell you.",
    date: "2026-03-20",
    readTime: "4 min read",
    category: "FX Explained",
    number: 6,
    illustration: "weekend",
    excerpt: "If you've ever checked a rate on Saturday and noticed it looks identical to Friday's, you're not imagining things. Weekend rates are different — and not always in your favour.",
    content: `
If you've ever checked an exchange rate on a Saturday and noticed it looks suspiciously identical to Friday's, you're not imagining things. Weekend exchange rates are different — and not always in your favour.

## Currency markets don't run on weekends

The foreign exchange market operates 24 hours a day, five days a week — from Sunday evening (when Asian markets open) to Friday evening (when New York closes). On Saturdays and Sundays, the major currency markets are effectively closed.

No new trades. No price discovery. No official rate being set.

## So what rate do banks use?

Banks and currency providers still need to offer a rate for weekend transactions — for card payments abroad, online transfers, and currency purchases. Most use Friday's closing rate, or a rate frozen at some point on Friday afternoon.

But here's the catch: they also add extra margin.

Because they can't hedge their risk over the weekend (markets are closed), they widen their spread to protect themselves against any gap that might open when markets reopen on Sunday night. You're essentially paying a premium for their uncertainty.

## The ECB rate and FX Ping's charts

The European Central Bank publishes official reference rates each business day at around 4pm Frankfurt time. On weekends, no new rate is published — so the Saturday and Sunday rates in FX Ping's historical charts show Friday's ECB rate, repeated.

This isn't a bug — it's an accurate reflection of the fact that no official rate exists on those days.

## What this means practically

**For card payments abroad:** Your bank converts at whatever rate they've frozen for the weekend, usually with a slightly wider margin than weekdays. You won't know the exact rate until the transaction settles.

**For transfers:** Most bank transfer systems don't process on weekends anyway, so the rate is often locked at Friday's level until Monday morning — by which point the market may have moved.

**For time-sensitive transfers:** If you need to send money and rates are moving, weekday transactions give you a live rate with tighter spreads. If you initiate a large transfer on a Friday afternoon, be aware you might not get a better rate until Monday.

## The practical upshot

Weekend rates are slightly less favourable than weekday rates, and slightly more unpredictable. For small everyday transactions it rarely matters. For larger amounts, timing a transfer for mid-week — when markets are liquid and spreads are tightest — is a reasonable habit to develop.
`
  },
  {
    slug: "what-is-a-currency-peg",
    title: "What is a currency peg — and what happens when it breaks?",
    subtitle: "Some currencies are fixed by design. Here's how, why, and what happens when it goes wrong.",
    date: "2026-03-21",
    readTime: "6 min read",
    category: "FX Explained",
    number: 7,
    illustration: "peg",
    excerpt: "Most exchange rates float freely. But some currencies are pegged — fixed to another currency at a set rate. Understanding pegs, and what happens when they break, is genuinely fascinating.",
    content: `
Most exchange rates float freely — they move up and down based on supply and demand. But some currencies are pegged: fixed to another currency at a set rate by their government or central bank. The UAE dirham has been fixed to the US dollar since 1997. The Hong Kong dollar has been pegged since 1983. Understanding pegs — and what happens when they break — is genuinely fascinating.

## Why peg a currency?

Pegging offers stability. For small, open economies heavily dependent on trade or tourism, a volatile exchange rate can be damaging. Businesses can't plan. Import prices swing unpredictably. Investment becomes riskier.

By fixing the rate to a stable major currency — usually the US dollar — a country essentially imports that currency's credibility. The UAE dirham at 3.6725 per dollar means every business and resident knows exactly what their currency is worth in dollar terms, today and tomorrow.

Pegs also help control inflation. If your currency is anchored to the dollar, you can't simply print money recklessly — doing so would put pressure on the peg.

## How does a country maintain a peg?

It requires constant intervention. The central bank buys and sells its own currency on the open market to keep the rate stable, using its foreign currency reserves.

If demand for the local currency falls (threatening to weaken it below the peg), the central bank buys it using dollar reserves, supporting the price. If demand rises too high, they sell local currency and buy dollars.

To sustain a peg, you need substantial foreign currency reserves and the political will to defend it. The moment markets believe a central bank can't or won't defend the peg, things get dangerous.

## What happens when a peg breaks

This is where it gets dramatic.

If a currency is pegged at a rate that no longer reflects economic reality — if the currency is chronically overvalued at its fixed rate — pressure builds. Investors and speculators bet against the peg, selling the currency in large volumes. The central bank burns through its reserves defending it. Eventually, it runs out of room.

**The Swiss franc in 2015** is the most dramatic recent example. Switzerland had capped the franc at 1.20 per euro to prevent it from strengthening too much (a strong franc hurts Swiss exporters). On 15 January 2015, the Swiss National Bank abruptly abandoned the cap. The franc instantly strengthened 20% against the euro — one of the largest single-day moves ever seen in a major currency. Traders who had bet against the cap made fortunes. Others who were on the wrong side were wiped out.

**The UK in 1992** — "Black Wednesday" — is the classic textbook case. Sterling was part of the European Exchange Rate Mechanism, pegged to the deutschmark at a rate many economists believed was too high. George Soros and other speculators bet billions that the UK would have to devalue. The UK government raised interest rates dramatically to defend the peg and spent billions in reserves. It wasn't enough. On 16 September 1992, the UK withdrew from the ERM. Sterling fell sharply. Soros reportedly made $1 billion in a single day.

## Pegs that hold

Not all pegs break dramatically. The UAE dirham peg has held for nearly 30 years, backed by massive oil revenues and dollar reserves. The Hong Kong dollar peg has survived multiple crises including the 1997 Asian financial crisis and the 2008 global financial crisis.

The difference is credibility, reserves, and whether the fixed rate actually reflects economic reality.

## What this means for FX Ping users

Pegged currencies appear on FX Ping with very stable rates — the AED/USD rate barely moves year to year. If you're tracking a pegged currency and the rate starts moving significantly, that's unusual and potentially significant news.

Freely floating currencies like GBP, EUR, AUD, and ZAR will show much more movement on the historical charts — that volatility is the market working normally.
`
  },
  {
    slug: "how-to-read-an-exchange-rate-table",
    title: "How to read an exchange rate table without getting confused",
    subtitle: "A practical walkthrough of what the numbers actually mean",
    date: "2026-03-22",
    readTime: "5 min read",
    category: "FX Explained",
    number: 8,
    illustration: "table",
    excerpt: "Exchange rate tables look simple — a list of currencies and numbers. But they trip people up more often than you'd expect. Here's a plain-English walkthrough.",
    content: `
Exchange rate tables look simple — a list of currencies and numbers. But they trip people up more often than you'd expect. Which way round is the rate? If the number is bigger, is that better or worse? What do all the decimal places mean?

Here's a plain-English walkthrough using FX Ping as the example.

## What the numbers mean

Every exchange rate is a ratio between two currencies. On FX Ping, you choose a **base currency** — let's say USD. Every rate in the table then tells you: **how many units of that currency does 1 USD buy?**

- USD/EUR: 0.9231 — 1 dollar buys 0.9231 euros
- USD/GBP: 0.7854 — 1 dollar buys 0.7854 pounds
- USD/JPY: 149.82 — 1 dollar buys 149.82 yen
- USD/AED: 3.6725 — 1 dollar buys 3.6725 dirhams

The base currency (USD in this case) is always worth exactly 1. Everything else is expressed relative to it.

## Bigger number = stronger base currency (against that pair)

If USD/JPY moves from 149 to 155, it means 1 dollar now buys more yen — the dollar got stronger against the yen. A higher number means the base currency buys more.

If USD/EUR moves from 0.92 to 0.88, it means 1 dollar now buys fewer euros — the dollar got weaker against the euro. A lower number means the base currency buys less.

## Why so many decimal places?

Small differences in exchange rates translate into large differences in money when you're moving significant sums. The difference between 1.2700 and 1.2750 on a £100,000 transfer is £393. So rates are quoted to 4 decimal places (called "pips" in trading) to capture meaningful precision.

For currencies with large nominal values (like JPY or KRW), the rate itself is a large number so fewer decimal places are needed — 149.82 JPY is already precise enough.

## What the margin calculator shows

FX Ping's margin field (the % input in the header) lets you model the cost of your bank or transfer provider's markup. Enter 2% for example, and every rate in the table shifts to show what you'd actually receive after a 2% margin is applied.

This is useful for comparison: the "raw" rate column shows the mid-market benchmark, and the margin-adjusted column shows real-world delivery.

## Switching the base currency

The base currency selector in the header changes the reference point for every rate in the table. Switch from USD to EUR, and suddenly every rate tells you how many units of each currency 1 euro buys.

This matters if you're thinking in your home currency. A British person should set GBP as the base — then USD showing 1.27 means £1 buys $1.27, which is the intuitive way to think about it.

## The historical chart pages

Clicking any currency takes you to its chart page, showing how the rate has moved over the past year (or more, as FX Ping's own database grows). The Y-axis shows the rate, the X-axis shows time, and a hover tooltip shows the exact rate on any given date.

A rising line means the base currency strengthened against that pair. A falling line means it weakened. That simple visual tells you more about a currency's trajectory than any single number.

## The one thing to remember

Exchange rates are always relative — they tell you about one currency in terms of another, never in isolation. "The pound is at 1.27" means nothing without knowing it's 1.27 dollars. Keep both sides of the pair in mind, and the numbers start to make intuitive sense.
`
  },
];

const today = new Date().toISOString().split('T')[0];
export const blogPosts = allPosts.filter(p => p.date <= today);

export const getPost = (slug) => blogPosts.find(p => p.slug === slug);
